import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ApprovalRequest } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { APPROVAL_BADGE, APPROVAL_STATUS } from './labels';

type Tab = 'pending' | 'decided' | 'mine';
const TABS: Record<Tab, string> = { pending: 'Por decidir', decided: 'Decididas', mine: 'Mías' };
const when = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'long' });

/** Aprobaciones (Motor 21): la decisión siempre es de una Persona, nunca de quien pidió. */
export function ApprovalsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canDecide = me.can('approvals.decide');
  const [params, setParams] = useSearchParams();
  const requested = params.get('list') as Tab | null;
  const tab: Tab = requested && TABS[requested] && (canDecide || requested === 'mine') ? requested : canDecide ? 'pending' : 'mine';
  const list = useAsync(
    (signal) => (churchId ? api.get<{ items: ApprovalRequest[] }>(`/approvals/churches/${churchId}/requests?list=${tab}`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId, tab],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  const tabs: Tab[] = canDecide ? ['pending', 'decided', 'mine'] : ['mine'];
  const empty: Record<Tab, string> = { pending: 'No hay nada por aprobar.', decided: 'Aún no hay decisiones.', mine: 'No has pedido ninguna aprobación.' };

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Aprobaciones</h1>
        <p className="muted">Inscripciones y solicitudes que necesitan el visto bueno de alguien.</p>
      </header>
      <MembershipRequestsLine churchId={churchId} />
      {tabs.length > 1 ? (
        <div className="tabs" role="tablist" aria-label="Aprobaciones">
          {tabs.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`tabs__tab${tab === t ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ list: t })}>
              {TABS[t]}
            </button>
          ))}
        </div>
      ) : null}
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' && list.data.length === 0 ? <EmptyState title={empty[tab]} /> : null}
      {list.status === 'ready'
        ? list.data.map((r) => <RequestCard key={r.id} churchId={churchId} request={r} mine={r.requestedBy.personId === me.personId} canDecide={canDecide} onChange={list.reload} />)
        : null}
    </div>
  );
}

/** Las personas que piden ser miembros se aprueban en Miembros; aquí solo se avisa si hay pendientes. */
function MembershipRequestsLine({ churchId }: { churchId: string }) {
  const me = useMe();
  const canApprove = me.permissions.some((p) => p.permission === 'members.approve' && p.scope !== 'self');
  const count = useAsync(
    (signal) =>
      canApprove
        ? api.get<{ items: unknown[] }>(`/multichurch/churches/${churchId}/memberships?status=pending`, signal).then((r) => r.items.length).catch(() => 0)
        : Promise.resolve(0),
    [churchId, canApprove],
  );
  if (count.status !== 'ready' || count.data === 0) return null;
  return (
    <Alert tone="info">
      {count.data === 1 ? 'Hay 1 solicitud de membresía' : `Hay ${count.data} solicitudes de membresía`}{' '}
      <Link className="btn btn--secondary" to="/app/miembros?tab=solicitudes">
        Ver en Miembros
      </Link>
    </Alert>
  );
}

function RequestCard({ churchId, request: r, mine, canDecide, onChange }: { churchId: string; request: ApprovalRequest; mine: boolean; canDecide: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/approvals/churches/${churchId}/requests/${r.id}`;

  async function run(path: string, body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`${base}/${path}`, body);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onReject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void run('reject', { note: String(new FormData(event.currentTarget).get('note') || '') || null });
  }

  return (
    <Card>
      <p className="approval__title">
        <strong>{r.title}</strong> <span className={APPROVAL_BADGE[r.status]}>{APPROVAL_STATUS[r.status]}</span>
      </p>
      {r.summary ? <p>{r.summary}</p> : null}
      <p className="muted small">
        Pidió {mine ? 'tú' : r.requestedBy.name} · {when(r.createdAt)}
        {r.decidedBy && r.decidedAt ? ` · Decidió ${r.decidedBy.name}, ${when(r.decidedAt)}` : ''}
      </p>
      {r.decisionNote ? <p className="small">“{r.decisionNote}”</p> : null}
      {error ? <Alert>{error}</Alert> : null}
      {r.status === 'pending' ? (
        <div className="actions">
          {r.link && canDecide ? (
            <Link className="btn btn--ghost" to={r.link}>
              <span>Ver detalle</span>
            </Link>
          ) : null}
          {canDecide && !mine && !rejecting ? (
            <>
              <Button busy={busy} onClick={() => run('approve')}>
                Aprobar
              </Button>
              <Button variant="secondary" onClick={() => setRejecting(true)}>
                No aprobar
              </Button>
            </>
          ) : null}
          {mine ? (
            <Button variant="ghost" busy={busy} onClick={() => run('withdraw')}>
              Retirar solicitud
            </Button>
          ) : null}
        </div>
      ) : null}
      {canDecide && mine && r.status === 'pending' ? <p className="muted small">Otra persona con permiso debe decidir tu solicitud.</p> : null}
      {rejecting ? (
        <form className="form" onSubmit={onReject} noValidate>
          <Field label="Motivo (lo verá quien pidió)" name="note" maxLength={1000} />
          <div className="actions">
            <Button type="submit" variant="danger" busy={busy}>
              Confirmar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setRejecting(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
